<template>
  <header
    v-clickoutside="closeHeaderMenu"
    :class="{
      'has-sidebar': isSidebarVisible,
      'header--active': isGameMenuOpened,
      'header--above': showRegionTooltip,
    }"
    class="header">
    <transition name="transition--opacity">
      <header-message
        v-if="shouldShowHeaderMessage && headerMessage.text"
        :can-be-closed="true"
        @close="closeHeaderMessage">
        <div v-html="headerMessage.text" />
      </header-message>
    </transition>

    <component
      :is="currentTemplate"
      :is-burger-active="isBurgerActive"
      :is-game-menu-opened="isGameMenuOpened"
      :is-mobile="isMobile"
      :user="user"
      :is-customer-authorized="isCustomerAuthorized"
      :filtered-account-links="filteredAccountLinks"
      class="header__container container"
      @hideSidebar="$emit('hideSidebar')"
      @setBurgerState="setMenuState('setBurgerState', $event)"
      @setGameMenuState="setMenuState('setGameMenuState', $event)"
      @setVisibleRegionTooltip="showRegionTooltip = $event"
      @header-select-open="$emit('hideSidebar')">
      <template #logo>
        <a
          class="header__logo"
          href="/"
          @click="navigateToMainPage">
          <img
            height="28"
            width="121"
            src="/images/logos/skycoach.svg"
            class="header__logo-pic">
        </a>
      </template>

      <template #burger>
        <base-burger
          v-if="!isPlatformApp"
          :is-active="isBurgerActive"
          @click="setMenuState('setBurgerState', $event)" />
      </template>

      <template #pc-game-menu>
        <transition name="header-menu">
          <main-header-p-c-menu-games
            v-if="!isMobile && isGameMenuOpened"
            class="header__menu"
            @close="closeHeaderMenu" />
        </transition>
      </template>

      <template #account-widget />
    </component>

    <main-header-seo-links
      v-if="popularHeaderLinks && popularHeaderLinks.length"
      :links="popularHeaderLinks"
      class="header-seo" />
  </header>
</template>

<script>
import { mapState, mapMutations, mapGetters } from 'vuex';
import IntercomApiDriver from 'Root/drivers/IntercomApiDriver';
import clickoutside from 'Root/directives/clickoutside';
import UserModel from 'Models/UserModel';
import SignInModel from 'Models/SignInModel';
import HeaderMessage from 'Components/ui/HeaderMessage.vue';
import BaseBurger from 'Components/ui/Burger/BaseBurger.vue';
import MainHeaderSeoLinks from 'Components/MainHeader/MainHeaderSeoLinks.vue';
import HeaderSelectWidget from 'Components/MainHeader/HeaderSelectWidget.vue';

import DefaultHeaderTemplate from './templates/DefaultHeaderTemplate.vue';
import ClientHeaderTemplate from './templates/ClientHeaderTemplate.vue';

const accountLinksByRoute = {
  client: ['user', 'email', 'logout'],
  default: ['user', 'orders', 'cashback', 'logout'],
};

export default {
  name: 'MainHeader',

  components: {
    BaseBurger,
    HeaderMessage,
    DefaultHeaderTemplate,
    ClientHeaderTemplate,
    MainHeaderSeoLinks,
    MainHeaderPCMenuGames: () => import('Components/MainHeader/MainHeaderPCMenuGames.vue'),
    HeaderSelectWidget,
  },

  directives: {
    ...clickoutside,
  },

  props: {
    isGameMenuOpened: {
      type: Boolean,
      default: false,
    },

    isBurgerActive: {
      type: Boolean,
      default: false,
    },

    isSidebarAvailable: {
      type: Boolean,
      default: false,
    },

    isSidebarVisible: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      showRegionTooltip: false,
      headerMessage: {
        id: null,
        text: null,
      },
      SignInModel,
    };
  },

  computed: {
    ...mapState(['userDevice', 'user']),
    ...mapState('products', ['popularHeaderLinks']),
    ...mapGetters(['isPlatformApp']),

    shouldShowHeaderMessage() {
      return false;
    },

    isMobile() {
      return this.userDevice.innerWidth < 1024;
    },

    currentTemplate() {
      if (this.$route.name.includes('client') && this.user) {
        return 'client-header-template';
      }

      return 'default-header-template';
    },

    isCustomerAuthorized() {
      return this.user && this.user?.role === UserModel.ROLES.CUSTOMER.id;
    },

    filteredAccountLinks() {
      if (this.$route.name.includes('client')) {
        return this.accountLinks.filter((link) => {
          if (!accountLinksByRoute.client.includes(link.id)
            || (this.isPlatformApp && link.id === 'logout')) {
            return false;
          }

          return true;
        });
      }

      return this.accountLinks.filter((link) => accountLinksByRoute.default.includes(link.id));
    },

    accountLinks() {
      if (!this.user) {
        return [];
      }

      return [
        {
          id: 'user',
          title: '',
        },
        {
          id: 'email',
          title: this.user.email,
          pic: '/images/icons/email.svg',
          callback: () => ({}),
        },
        {
          id: 'orders',
          title: 'My Orders',
          pic: '/images/icons/paper-list.svg',
          callback: () => this.$router.push({ name: 'client__orders' }),
        },
        {
          id: 'cashback',
          title: 'Cashback',
          pic: '/images/icons/money-return.svg',
          callback: () => {
            if (this.$route.name !== 'cashback') {
              this.$router.push({ name: 'cashback' });
            }
          },
        },
        {
          id: 'logout',
          title: 'Log out',
          pic: '/images/icons/login.svg',
          callback: () => this.logout(),
        },
      ];
    },
  },

  watch: {
    isMobile(newValue) {
      if (!newValue) {
        document.body.classList.remove('no-scroll');
      }
    },
  },

  methods: {
    ...mapMutations(['mutateUser']),

    navigateToMainPage(event) {
      event.preventDefault();
      if (this.$route.name !== 'root' && !this.isPlatformApp) {
        this.$router.push({ name: 'root' });
      }
    },

    closeHeaderMessage() {
      if (typeof window !== 'undefined' && 'sessionStorage' in window) {
        sessionStorage.setItem(`headerMessageClosed-${this.headerMessage.id}`, '1');
      }

      this.headerMessage.text = null;
    },

    setMenuState(menu, state) {
      this.$emit(menu, state);
    },

    closeHeaderMenu() {
      if (!this.isMobile) {
        this.setMenuState('setGameMenuState', false);
        this.setMenuState('setBurgerState', false);
      }
    },

    accountWidgetHandler(event) {
      event.callback?.();
    },

    async logout() {
      const { data } = await this.$axios.post('/shop/user/logout');

      if (data.info === 'ok') {
        IntercomApiDriver.shutdown(); /* Нужно, чтобы очистить всю инфу о пользователе, в т.ч. и куки */
        localStorage.removeItem(UserModel.SET_PASSWORD_LATER_TOKEN_NAME);
        window.location.href = '/';
      }
    },
  },
};
</script>

<style lang="scss">
  // New year sizes
  //.header__logo-pic {
  //  height: 32px;
  //  width: auto;
  //  margin-top: -4px;
  //}

  .header__container {
    display: grid;
    grid-gap: 16px;
    align-items: center;
    height: 64px;
    position: relative;
    z-index: 17;
  }

  .header__logo {
    display: flex;
    grid-area: logo;
    align-items: center;
    flex-shrink: 0;
    margin-right: auto;
  }

  .header__burger {
    grid-area: burger;
    position: relative;
    z-index: 6;
  }

  .header__account{
    grid-area: account;
  }
</style>

<style lang="scss" scoped>
  .header {
    position: relative;
    z-index: 10;
  }

  .header--above {
    z-index: 1001;
  }

  .header-menu-enter-active,
  .header-menu-leave-active {
    transition: .25s ease-out;
    transition-property: transform, opacity;
  }

  .header-menu-enter, .header-menu-leave-to {
    opacity: 0;
  }

  .header-menu-enter {
    transform: translateY(-5%);
  }

  .header-menu-enter-to, .header-menu-leave {
    opacity: 1;
    transform: translateY(0);
  }

  .header-seo {
    grid-column: 1 / -1;
  }

  @media screen and #{$screen-more-or-1024} {
    .header__menu {
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      width: calc(100% - var(--padding) * 2);
      height: auto;
    }

    .header.has-sidebar {
      .header__menu {
        width: 100%;
      }
    }
  }

  @media screen and #{$screen-more-or-1366} {
    .header__container {
      height: $size-pc-header-height;
    }

    .header__logo-pic {
      height: 40px; // 50px - new year size
      width: auto;
      // margin-top: -8px; - new year size
    }
  }

  @media screen and #{$screen-more-or-1366} and #{$screen-less-than-1920} {
    .header.has-sidebar .header__container {
      padding: 0 var(--padding) 0 $padding-pc-sidebar;
      max-width: 100%;
    }
  }

  @media screen and #{$screen-more-or-1920} {
    .header {
      display: grid;
      grid-template-columns: 1fr minmax(auto, $size-container-max-width) 1fr;
    }

    .header.has-sidebar {
      grid-template-columns: minmax(250px, 1fr) minmax(auto, $size-container-max-width) 1fr;
    }

    .header__container {
      grid-column: 2;
    }

    .header.has-sidebar {
      .header__menu {
        width: calc(100% - var(--padding) * 2);
      }
    }
  }
</style>
